import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, List, Select, Tooltip, Spin } from 'antd';
import { getApi, postApi } from 'apis';
import Buttons from 'components/Global/Buttons';
import { currencyData } from 'constants/CurrencyData';
import { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { invalidText, toastText } from 'utils/utils';
import { fetchConstantDropdownAction } from '../../../../../redux/actions/constantDropdownAction';
import AddModal from '../AddModal';
import styles from './index.module.scss';
import { SideDrawerBodyProps } from './types';
import SelectDropdown from 'components/Global/SelectDropdown';
import { fetchCompanyWizard } from '../../../../../redux/actions/payrollSettingWizardAction';
import { dataApi } from 'Api/data';
import { payGroup } from 'Api/payGroup';
import { getAccessibleBranchForUser } from 'Api/masters/branch-code';

interface ISelectOption {
	value: string;
	label: string;
}

interface IItem {
	id: string;
	description: string;
}

const AddPayGroupBody: FC<SideDrawerBodyProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [isSelected, setIsSelected] = useState<boolean>();

	// ✅ NEW: Track added and removed employees for delta updates
	const [addedEmployeeIds, setAddedEmployeeIds] = useState<Set<string>>(
		new Set()
	);
	const [removedEmployeeIds, setRemovedEmployeeIds] = useState<Set<string>>(
		new Set()
	);

	const { closeDrawerByAnimation, fetchPayGroup, editSelected } = props;

	const [selectedBranch, setSelectedBranch] = useState<string[]>(
		editSelected?.branches || []
	);
	const [selectedDepartment, setSelectedDepartment] = useState<any[]>(
		editSelected?.departments || []
	);
	const [payGroupName, setPayGroupName] = useState<string>('');
	const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [selectedCurrency, setSelectedCurrency] = useState<any>(
		editSelected ? editSelected?.currency : null
	);

	// ✅ NEW: Pagination state for employees
	const [employeePage, setEmployeePage] = useState<number>(1);
	const [employeePageSize] = useState<number>(20);
	const [totalEmployees, setTotalEmployees] = useState<number>(0);
	const [totalPayGroupEmployees, setTotalPayGroupEmployees] =
		useState<number>(0);

	const [isEmployeesLoading, setIsEmployeesLoading] =
		useState<boolean>(false);
	const [employeeCountChange, setEmployeeCountChange] = useState<number>(0);

	const [employees, setEmployees] = useState<any[]>([]);
	const [searchTerm, setSearchTerm] = useState('');

	const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);
	const [selectedEmployee, setSelectedEmployee] = useState<string[]>([]);
	const dispatch = useDispatch<AppDispatch>();

	// ✅ OPTIMIZED: Load employees on demand for edit mode
	const [payGroupEmployees, setPayGroupEmployees] = useState<any[]>([]);
	const [isLoadingPayGroupEmployees, setIsLoadingPayGroupEmployees] =
		useState(false);

	const [earningsOptions, setEarningsOptions] = useState<ISelectOption[]>([]);
	const [selectedEarnings, setSelectedEarnings] = useState<string[]>([]);

	const [deductionOptions, setDeductionOptions] = useState<ISelectOption[]>(
		[]
	);
	const [selectedDeductions, setSelectedDeductions] = useState<string[]>([]);

	const [checkedEarnings, setCheckedEarnings] = useState<Set<string>>(
		new Set()
	);
	const [checkedDeductions, setCheckedDeductions] = useState<Set<string>>(
		new Set()
	);

	const [accessibleBranches, setAccessibleBranches] = useState<
		ISelectOption[]
	>([]);
	const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);

	const { isLoading } = useSelector((state: any) => state.constantDropdown);

	const buttons = useMemo(
		() => [
			{
				text: `Cancel`,
				isLoading: false,
				className: 'btn-cancel',
				isSubmit: true,
				disabled: loading,
				fontSize: '1.8rem',
				minWidth: '12rem',
				minHeight: '4rem',
				onclick: () => {
					closeDrawerByAnimation();
				},
			},
			{
				text: `Save`,
				isLoading: loading,
				className: 'btn-blue',
				isSubmit: true,
				fontSize: '1.8rem',
				minWidth: '12rem',
				minHeight: '4rem',
				disabled: false,
				onclick: () => {},
				font: '16px',
			},
		],
		[loading, closeDrawerByAnimation]
	);

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);

	// ✅ OPTIMIZED: Memoize currency options
	const mappedCurrencyOptions = useMemo(
		() =>
			dropdownOptions?.currencyData?.map((currency: any) => ({
				value: currency.value,
				label: invalidText(currency.value)
					? 'Select Currency'
					: `${currency.label} - ${
							currencyData[currency.label] || currency.label
					  }`,
			})),
		[dropdownOptions?.currencyData]
	);

	// ✅ OPTIMIZED: Memoize handlers
	const handleEarningsChange = useCallback((values: string[]) => {
		setCheckedEarnings((prev) => {
			const updated = new Set(prev);
			values.forEach((id) => updated.add(id));
			return updated;
		});
		setSelectedEarnings(values);
	}, []);

	const handleDeductionsChange = useCallback((values: string[]) => {
		setCheckedDeductions((prev) => {
			const updated = new Set(prev);
			values.forEach((id) => updated.add(id));
			return updated;
		});
		setSelectedDeductions(values);
	}, []);

	const handleSubmit = async (values: { name: string }) => {
		setLoading(true);

		if (editSelected) {
			try {
				let data: any = {
					payGroupId: editSelected?.id,
					name: values.name,
					addedEmployeeIds: Array.from(addedEmployeeIds),
					removedEmployeeIds: Array.from(removedEmployeeIds),
				};
				const response = await postApi('/payGroup/update', data);

				const earningIds = Array.from(checkedEarnings).map((id) => ({
					id,
					isChecked: selectedEarnings.includes(id),
				}));

				const deductionIds = Array.from(checkedDeductions).map(
					(id) => ({
						id,
						isChecked: selectedDeductions.includes(id),
					})
				);

				await payGroup.earningAndDeductionForPayGroupEmployees({
					payGroupId: editSelected?.id,
					earningIds,
					deductionIds,
				});

				if (response?.data) {
					closeDrawerByAnimation();
					fetchPayGroup();
					toastText('Pay Group updated successfully', 'success');
				}
			} catch (error: any) {
				let message =
					error?.response?.data?.message ||
					`Something went wrong in updating pay group`;
				toastText(message, 'error');
			} finally {
				setLoading(false);
			}
		} else {
			try {
				if (selectedCurrency) {
					setIsSelected(true);
					setPayGroupName(values.name);
					await fetchEmployeeData(values.name, true);
				} else {
					setIsSelected(false);
				}
			} catch (error: any) {
				let message =
					error?.response?.data?.message ||
					`Something went wrong in creating pay group`;
				toastText(message, 'error');
			} finally {
				setLoading(false);
			}
		}
	};

	const onFinishFailed = () => {
		if (selectedCurrency) {
			setIsSelected(true);
		} else {
			setIsSelected(false);
		}
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
	};

	// ✅ OPTIMIZED: Memoize department filtering
	useEffect(() => {
		if (!selectedBranch.length) {
			setDepartmentOptions([]);
			return;
		}

		const allDepartments: any[] = [];
		selectedBranch.forEach((branchId) => {
			const departments = dropdownOptions?.department?.filter(
				(b: any) => b.branchId === branchId
			);
			if (departments) {
				allDepartments.push(...departments);
			}
		});

		// Remove duplicates
		const uniqueDepartments = allDepartments.reduce(
			(acc: any[], current: any) => {
				if (!acc.some((dept) => dept.value === current.value)) {
					acc.push(current);
				}
				return acc;
			},
			[]
		);

		setDepartmentOptions(uniqueDepartments);
	}, [selectedBranch, dropdownOptions?.department]);

	const fetchEmployeeData = async (
		payGroupName: string,
		isSubmit: boolean
	) => {
		setIsEmployeesLoading(true);
		try {
			const query = {
				currencyId: selectedCurrency || '',
				branchIds: selectedBranch,
				departmentIds: selectedDepartment,
			};

			const response = await getApi('/payGroup/employee', query);
			const employeeData = response.data.data || [];

			setEmployees(employeeData);
			setTotalEmployees(employeeData.length);

			const options = employeeData.map((employee: any) => ({
				value: employee.id,
				label: `${employee.employeeId} - ${employee.firstName} ${
					employee.midName ?? ''
				} ${employee.lastName} `,
			}));
			setEmployeeOptions(options);

			if (employeeData.length === 0 && isSubmit) {
				const _currency =
					mappedCurrencyOptions?.find(
						(option: any) => option.value === selectedCurrency
					) || {};
				const _branches = dropdownOptions?.branchCode.filter(
					(branch: any) => selectedBranch.includes(branch.value)
				);
				const _departments = departmentOptions.filter(
					(department: any) =>
						selectedDepartment.includes(department.value)
				);
				const currencyId = _currency?.value || '';
				const branchIds = _branches.map((branch: any) => branch.value);
				const departmentIds = _departments.map(
					(department) => department.value
				);
				const employeeIds = employees.map((employee) => employee.id);

				const data = {
					name: payGroupName,
					branchIds,
					departmentIds,
					employeeIds,
					companyCurrencyId: currencyId,
				};

				const response = await postApi('/payGroup/', data);

				await payGroup.earningAndDeductionForPayGroupEmployees({
					payGroupId: response.data.data.id,
					earningIds: selectedEarnings.map((id) => ({
						id,
						isChecked: true,
					})),
					deductionIds: selectedDeductions.map((id) => ({
						id,
						isChecked: true,
					})),
				});

				dispatch(fetchCompanyWizard());
				let message =
					response?.data?.message || 'Pay group created successfully';
				toastText(message, 'success');

				fetchPayGroup();
				closeDrawerByAnimation();
			} else {
				!editSelected && setIsModalOpen(true);
			}
		} catch (err) {
		} finally {
			setIsEmployeesLoading(false);
		}
	};

	// ✅ OPTIMIZED: Lazy load employees with pagination for edit mode
	const fetchPayGroupEmployees = useCallback(
		async (page: number = 1) => {
			if (!editSelected?.id) return;

			try {
				setIsLoadingPayGroupEmployees(true);

				// ✅ NEW: Use pagination endpoint
				const response = await getApi(
					`/payGroup/${editSelected.id}/employees`,
					{
						page,
						pageSize: employeePageSize,
						search: searchTerm,
					}
				);

				const { data, total } = response.data;
				setTotalPayGroupEmployees(total || 0);

				if (page === 1) {
					setEmployeeCountChange(0);
					setPayGroupEmployees(data);
				} else {
					setPayGroupEmployees((prev) => [...prev, ...data]);
				}
			} catch (err: any) {
				const message =
					err?.response?.data?.message || 'Failed to fetch employees';
				toastText(message, 'error');
			} finally {
				setIsLoadingPayGroupEmployees(false);
			}
		},
		[editSelected?.id, employeePageSize, searchTerm]
	);

	// ✅ FIXED: Remove employee handler with delta tracking
	const handleRemoveEmployee = useCallback(
		(employeeId: string) => {
			// Find the employee being removed
			const removedEmployee = payGroupEmployees.find(
				(employee) => employee.employeeId === employeeId
			);

			if (!removedEmployee) return;

			// Update delta tracking
			if (addedEmployeeIds.has(removedEmployee.id)) {
				// If it was added in this session, just un-mark it as added
				const newAdded = new Set(addedEmployeeIds);
				newAdded.delete(removedEmployee.id);
				setAddedEmployeeIds(newAdded);
			} else {
				// Otherwise, mark it as removed
				setRemovedEmployeeIds((prev) => {
					const newRemoved = new Set(prev);
					newRemoved.add(removedEmployee.id);
					return newRemoved;
				});
			}

			// Remove from pay group employees list
			setPayGroupEmployees((prevEmployees) =>
				prevEmployees.filter(
					(employee) => employee.employeeId !== employeeId
				)
			);
			setEmployeeCountChange((prev) => prev - 1);

			// Remove from selected employees
			setSelectedEmployee((prevSelected) =>
				prevSelected.filter((id) => id !== removedEmployee.id)
			);

			// Add back to dropdown options
			const employeeOption = {
				value: removedEmployee.id,
				label: `${removedEmployee.employeeId} - ${
					removedEmployee.firstName
				} ${removedEmployee.midName ?? ''} ${removedEmployee.lastName}`,
			};

			setEmployeeOptions((prevOptions) => {
				// Check if already exists to avoid duplicates
				const exists = prevOptions.some(
					(opt) => opt.value === removedEmployee.id
				);
				if (exists) return prevOptions;

				return [...prevOptions, employeeOption];
			});
		},
		[payGroupEmployees, addedEmployeeIds]
	);

	const modalRemoveEmployee = useCallback(
		(employeeId: string) => {
			// Find the employee being removed
			const removedEmployee = employees.find(
				(employee) => employee.employeeId === employeeId
			);

			if (!removedEmployee) return;

			// Remove from employees array (used by modal)
			setEmployees((prevEmployees) =>
				prevEmployees.filter(
					(employee) => employee.employeeId !== employeeId
				)
			);

			// Update total count
			setTotalEmployees((prev) => prev - 1);
		},
		[employees]
	);

	// ✅ FIXED: Add employee handler
	// ✅ FIXED: Add employee handler with better state management
	// ✅ FIXED: Build employee from employeeOptions
	const handleEmployeeSelect = (employeeIds: string[]) => {
		const newlySelectedIds = employeeIds.filter(
			(id) => !selectedEmployee.includes(id)
		);

		if (newlySelectedIds.length === 0) {
			return;
		}

		// ✅ Build from employeeOptions instead of employees array
		const selectedEmployees = newlySelectedIds
			.map((id) => {
				// First try to find in employees array
				let emp = employees.find((e) => e.id === id);

				// If not found, build from employeeOptions
				if (!emp) {
					const option = employeeOptions.find(
						(opt) => opt.value === id
					);
					if (option) {
						// Parse label: "employeeId - firstName midName lastName"
						const labelParts = option.label.split(' - ');
						const employeeId = labelParts[0]?.trim() || '';
						const fullName = labelParts[1]?.trim() || '';
						const nameParts = fullName.split(' ');

						emp = {
							id: id,
							employeeId: employeeId,
							firstName: nameParts[0] || '',
							midName: nameParts.length === 3 ? nameParts[1] : '',
							lastName:
								nameParts.length === 3
									? nameParts[2]
									: nameParts[1] || '',
						};
					}
				}

				return emp
					? {
							id: emp.id,
							employeeId: emp.employeeId,
							firstName: emp.firstName,
							midName: emp.midName ?? '',
							lastName: emp.lastName,
					  }
					: null;
			})
			.filter(Boolean); // Remove nulls

		if (selectedEmployees.length === 0) {
			return;
		}

		// Calculate filtered list based on current state
		const filtered = selectedEmployees.filter(
			(selectedEmployee) =>
				!payGroupEmployees.some(
					(employee) => employee.id === selectedEmployee?.id
				)
		);

		if (filtered.length > 0) {
			setPayGroupEmployees((prevEmployees) => [
				...prevEmployees,
				...filtered,
			]);
			setEmployeeCountChange((prev) => prev + filtered.length);
		}
		// Update delta tracking
		const newAdded = new Set(addedEmployeeIds);
		const newRemoved = new Set(removedEmployeeIds);

		filtered.forEach((emp) => {
			if (!emp) return;
			if (newRemoved.has(emp.id)) {
				// If it was pending removal, just un-mark it
				newRemoved.delete(emp.id);
			} else {
				// Otherwise mark as added
				newAdded.add(emp.id);
			}
		});

		setAddedEmployeeIds(newAdded);
		setRemovedEmployeeIds(newRemoved);

		// Remove from dropdown options
		setEmployeeOptions((prevOptions) => {
			const filteredOptions = prevOptions.filter(
				(option) => !newlySelectedIds.includes(option.value)
			);

			return filteredOptions;
		});

		// Update selected employee state
		setSelectedEmployee(employeeIds);
	};

	// ✅ OPTIMIZED: Load master data once
	useEffect(() => {
		const fetchMasterDropdowns = async () => {
			try {
				const [earningsRes, deductionsRes] = await Promise.all([
					dataApi.getMasterEarnings(),
					dataApi.getMasterDeductions(),
				]);

				setEarningsOptions(
					earningsRes.data.data.map((item: IItem) => ({
						value: item.id,
						label: item.description,
					}))
				);

				setDeductionOptions(
					deductionsRes.data.data.map((item: IItem) => ({
						value: item.id,
						label: item.description,
					}))
				);
			} catch (err) {
				toastText('Unable to load earnings/deductions', 'error');
			}
		};

		fetchMasterDropdowns();
	}, []);

	// ✅ OPTIMIZED: Only fetch employees when drawer opens in edit mode
	useEffect(() => {
		if (editSelected?.id) {
			fetchPayGroupEmployees(1);
		}
	}, [editSelected?.id, fetchPayGroupEmployees]);

	useEffect(() => {
		if (editSelected) {
			fetchEmployeeData(editSelected.name, false);
		}
	}, [editSelected]);

	// ✅ OPTIMIZED: Memoize filtered options
	const getFilteredEmployeeOptions = useMemo(() => {
		return employeeOptions.filter((option) => {
			const notSelected = !selectedEmployee.includes(option.value);
			const matchesSearch = searchTerm
				? option.label.toLowerCase().includes(searchTerm.toLowerCase())
				: true;

			return notSelected && matchesSearch;
		});
	}, [employeeOptions, selectedEmployee, searchTerm]);

	useEffect(() => {
		if (editSelected) {
			setSelectedEarnings(editSelected.earningIds || []);
			setSelectedDeductions(editSelected.deductionIds || []);

			setCheckedEarnings(new Set(editSelected.earningIds || []));
			setCheckedDeductions(new Set(editSelected.deductionIds || []));
		}
	}, [editSelected]);

	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	const fetchAccessibleBranches = async () => {
		try {
			setIsBranchesLoading(true);

			const response = await getAccessibleBranchForUser();

			const branches: ISelectOption[] = response?.data?.data ?? [];
			branches.shift();
			setAccessibleBranches(branches);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Failed to fetch accessible branches';
			toastText(message, 'error');
		} finally {
			setIsBranchesLoading(false);
		}
	};

	useEffect(() => {
		fetchAccessibleBranches();
	}, []);

	const handleLoadMore = useCallback(() => {
		const hasMore =
			employeePage * employeePageSize < totalPayGroupEmployees;
		if (!isLoadingPayGroupEmployees && hasMore) {
			fetchPayGroupEmployees(employeePage + 1);
			setEmployeePage((prev) => prev + 1);
		}
	}, [
		isLoadingPayGroupEmployees,
		totalPayGroupEmployees,
		fetchPayGroupEmployees,
		employeePage,
		employeePageSize,
	]);

	useEffect(() => {
		if (
			!isLoadingPayGroupEmployees &&
			payGroupEmployees.length === 0 &&
			totalPayGroupEmployees > 0
		) {
			handleLoadMore();
		}
	}, [
		payGroupEmployees.length,
		isLoadingPayGroupEmployees,
		totalPayGroupEmployees,
		handleLoadMore,
	]);

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const loadingMoreRef = useRef(false);

	const handleScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			const target = e.target as HTMLElement;
			const isNearBottom =
				target.scrollTop + target.clientHeight >=
				target.scrollHeight - 50;

			if (
				isNearBottom &&
				!loadingMoreRef.current &&
				!isLoadingPayGroupEmployees
			) {
				if (payGroupEmployees.length < totalPayGroupEmployees) {
					loadingMoreRef.current = true;
					handleLoadMore();
					// Reset after a delay
					setTimeout(() => {
						loadingMoreRef.current = false;
					}, 500);
				}
			}
		},
		[
			payGroupEmployees.length,
			totalPayGroupEmployees,
			isLoadingPayGroupEmployees,
			handleLoadMore,
		]
	);

	return (
		<>
			<p className={styles['form-container-head-warning']}>
				<b>
					<sup>*</sup>
				</b>{' '}
				Indicated mandatory fields
			</p>
			<div className={styles['side-drawer-body']}>
				<Form
					name="basic"
					className={styles['side-drawer-form']}
					labelCol={{ span: 8 }}
					wrapperCol={{ span: 16 }}
					style={{ maxWidth: 600 }}
					initialValues={editSelected}
					onFinishFailed={onFinishFailed}
					onFinish={handleSubmit}
					autoComplete="off"
				>
					<div className={styles['side-drawer-form__inputs']}>
						<label
							className={styles['side-drawer-form__role--label']}
						>
							{'Pay Group Name'}{' '}
							<span className="required-color">*</span>
						</label>
						<Form.Item
							name={'name'}
							rules={[
								{
									required: true,
									message: 'Pay group name required',
								},
								{
									validator: (_: any, value: string) => {
										if (value && value.includes('/')) {
											return Promise.reject(
												new Error(
													'Pay group name cannot contain "/"'
												)
											);
										}
										return Promise.resolve();
									},
								},
							]}
							wrapperCol={{ span: 24 }}
						>
							<Input
								className="remove-antd-input-effect"
								maxLength={150}
								size="large"
								disabled={false}
								type={'text'}
							/>
						</Form.Item>
					</div>

					{!editSelected && (
						<>
							<div className={styles['side-drawer-form__inputs']}>
								<Form.Item
									name={'currency'}
									wrapperCol={{ span: 24 }}
								>
									<label
										className={
											styles[
												'side-drawer-form__role--label'
											]
										}
									>
										{'Currency'}{' '}
										<span className="required-color">
											*
										</span>
									</label>

									<Select
										placeholder="Select Currency"
										className={
											styles['side-drawer-form--select']
										}
										size="large"
										disabled={editSelected || isLoading}
										loading={isLoading}
										value={selectedCurrency}
										onSelect={(currency) =>
											setSelectedCurrency(currency)
										}
										options={mappedCurrencyOptions}
									/>
									{isSelected === false && (
										<p className="ant-form-item-explain-error">
											Currency required
										</p>
									)}
								</Form.Item>
							</div>

							<div className={styles['side-drawer-form__inputs']}>
								<Form.Item
									name={'branchId'}
									wrapperCol={{ span: 24 }}
								>
									<label
										className={
											styles[
												'side-drawer-form__role--label'
											]
										}
									>
										{'Select Branch'}
									</label>

									<SelectDropdown
										defaultOption={false}
										defaultLabel="Select Branch"
										placeholder="Select Branch"
										options={accessibleBranches}
										value={selectedBranch}
										size="large"
										required={false}
										mode="multiple"
										helperText="Branch is required"
										loading={isBranchesLoading}
										label=""
										onChange={(branches: any) => {
											setSelectedBranch(branches);
											setSelectedDepartment([]);
											setIsSelected(true);
										}}
										disabled={
											editSelected || isBranchesLoading
										}
										isError={false}
									/>
								</Form.Item>
							</div>

							<div className={styles['side-drawer-form__inputs']}>
								<Form.Item
									name={'department'}
									wrapperCol={{ span: 24 }}
								>
									<label
										className={
											styles[
												'side-drawer-form__role--label'
											]
										}
									>
										{'Select Department'}
									</label>

									<SelectDropdown
										defaultOption={false}
										defaultLabel="Select Department"
										placeholder="Select Department"
										options={departmentOptions}
										value={selectedDepartment}
										size="large"
										required={false}
										mode="multiple"
										helperText="Department is required"
										label=""
										onChange={(departments: any) =>
											setSelectedDepartment(departments)
										}
										disabled={
											selectedBranch.length === 0 ||
											!!editSelected
										}
										isError={false}
									/>
								</Form.Item>
							</div>
						</>
					)}

					<div className={styles['side-drawer-form__inputs']}>
						<Form.Item
							initialValue={editSelected?.earningIds || []}
							wrapperCol={{ span: 24 }}
						>
							<label
								className={
									styles['side-drawer-form__role--label']
								}
							>
								Select Earnings
								<Tooltip
									title="Use this feature to add the selected earnings to all employees of this pay group."
									placement="right"
								>
									<InfoCircleOutlined
										style={{
											paddingInline: '0.5rem',
											color: '#584495',
											cursor: 'pointer',
										}}
									/>
								</Tooltip>
							</label>
							<SelectDropdown
								mode="multiple"
								placeholder="Select Earnings"
								size="large"
								options={earningsOptions}
								value={selectedEarnings}
								onChange={(value) =>
									handleEarningsChange(value as string[])
								}
								required={false}
								helperText="Earning is required"
								isError={false}
								className={styles['side-drawer-form--select']}
							/>
						</Form.Item>
					</div>

					<div className={styles['side-drawer-form__inputs']}>
						<Form.Item
							initialValue={editSelected?.deductionIds || []}
							wrapperCol={{ span: 24 }}
						>
							<label
								className={
									styles['side-drawer-form__role--label']
								}
							>
								Select Deductions
								<Tooltip
									title="Use this feature to add the selected deductions to all employees of this pay group."
									placement="right"
								>
									<InfoCircleOutlined
										style={{
											paddingInline: '0.5rem',
											color: '#584495',
											cursor: 'pointer',
										}}
									/>
								</Tooltip>
							</label>
							<SelectDropdown
								mode="multiple"
								placeholder="Select Deductions"
								size="large"
								options={deductionOptions}
								value={selectedDeductions}
								onChange={(value) =>
									handleDeductionsChange(value as string[])
								}
								required={false}
								helperText="Earning is required"
								isError={false}
								className={styles['side-drawer-form--select']}
							/>
						</Form.Item>
					</div>

					{editSelected && (
						<div className={styles['side-drawer-form__inputs']}>
							<Form.Item
								name={'employee'}
								wrapperCol={{ span: 24 }}
							>
								<label
									className={
										styles['side-drawer-form__role--label']
									}
								>
									{'Select Employee'}
								</label>
								<Select
									mode="multiple"
									placeholder="Search/Add Employee"
									className={
										styles['side-drawer-form--select']
									}
									size="large"
									loading={isEmployeesLoading}
									value={selectedEmployee}
									onChange={(employeeIds) => {
										setSelectedEmployee(employeeIds);
										handleEmployeeSelect(employeeIds);
									}}
									tagRender={(props) => (
										<span style={{ display: 'none' }}>
											{props.label}
										</span>
									)}
									showSearch
									onSearch={(value) => setSearchTerm(value)}
									filterOption={false}
									notFoundContent="No employees available"
									optionLabelProp="label"
									disabled={employeeOptions.length === 0}
									dropdownRender={(menu) => (
										<div
											style={{
												maxHeight: '200px',
												overflowY: 'auto',
												padding: 8,
											}}
										>
											{employeeOptions.length > 0 ? (
												<List
													dataSource={
														getFilteredEmployeeOptions
													}
													renderItem={(item) => (
														<List.Item
															style={{
																display: 'flex',
																justifyContent:
																	'space-between',
																borderBottom:
																	'1px solid #f0f0f0',
															}}
															actions={[
																<Button
																	type="default"
																	key={`add-${item.value}`}
																	onClick={() => {
																		// Add to selected employees
																		setSelectedEmployee(
																			(
																				prev
																			) => [
																				...prev,
																				item.value,
																			]
																		);

																		// This will trigger handleEmployeeSelect
																		handleEmployeeSelect(
																			[
																				...selectedEmployee,
																				item.value,
																			]
																		);
																	}}
																	style={{
																		borderColor:
																			'#584495',
																		color: '#584495',
																	}}
																>
																	Add
																</Button>,
															]}
														>
															{item.label}
														</List.Item>
													)}
												/>
											) : (
												<div
													style={{
														textAlign: 'center',
														padding: '8px 0',
													}}
												>
													No employees available
												</div>
											)}
										</div>
									)}
								/>
							</Form.Item>
						</div>
					)}

					{editSelected && (
						<div>
							<div className={styles['side-drawer-form__inputs']}>
								<div
									style={{
										marginBottom: '8px',
										fontWeight: 'bold',
									}}
								>
									Total number of Employees:{' '}
									{totalPayGroupEmployees +
										employeeCountChange}
								</div>
								<Spin spinning={isLoadingPayGroupEmployees}>
									<div
										ref={scrollContainerRef}
										style={{
											maxHeight: '230px',
											overflowY: 'auto',
										}}
										onScroll={handleScroll}
									>
										<List
											dataSource={payGroupEmployees}
											renderItem={(item) => (
												<List.Item
													actions={[
														<Button
															key={`remove-${item.employeeId}`}
															type="text"
															icon={
																<CloseOutlined
																	style={{
																		color: '#4a4a4a',
																	}}
																/>
															}
															onClick={() =>
																handleRemoveEmployee(
																	item.employeeId
																)
															}
														/>,
													]}
												>
													{item.firstName}{' '}
													{item.midName ?? ''}{' '}
													{item.lastName} -{' '}
													<span
														style={{
															color: '#584495',
														}}
													>
														{item.employeeId}
													</span>
												</List.Item>
											)}
										/>
										{isLoadingPayGroupEmployees &&
											payGroupEmployees.length > 0 && (
												<div
													style={{
														textAlign: 'center',
														padding: '10px',
													}}
												>
													<Spin size="small" />
												</div>
											)}
									</div>
								</Spin>
							</div>
						</div>
					)}

					<div className={styles['side-drawer-form__buttons']}>
						<Form.Item>
							<Buttons buttons={buttons} />
						</Form.Item>
					</div>
				</Form>
			</div>

			{isModalOpen && (
				<AddModal
					isModalOpen={isModalOpen}
					onClose={handleModalClose}
					currency={
						mappedCurrencyOptions?.find(
							(option: any) => option.value === selectedCurrency
						) || {}
					}
					branches={dropdownOptions?.branchCode.filter(
						(branch: any) => selectedBranch.includes(branch.value)
					)}
					departments={departmentOptions.filter((department: any) =>
						selectedDepartment.includes(department.value)
					)}
					totalEmployees={totalEmployees}
					employees={employees}
					onRemoveEmployee={modalRemoveEmployee}
					payGroupName={payGroupName}
					fetchPayGroup={fetchPayGroup}
					closeDrawerByAnimation={closeDrawerByAnimation}
					earningIds={selectedEarnings.map((id) => ({
						id,
						isChecked: true,
					}))}
					deductionIds={selectedDeductions.map((id) => ({
						id,
						isChecked: true,
					}))}
				/>
			)}
		</>
	);
};

export default AddPayGroupBody;
